// The v2 board's body (GRO-276): the one-line strip, then one panel holding the scope line (and
// the AE view's controls) and the table. The board's own filter bar sits above, in ExecutionView. Today's chrome and slide-over panel are unchanged; ExecutionView keeps the
// data and this only arranges it.

import { Profiler } from 'react';
import type { ProfilerOnRenderCallback, ReactNode } from 'react';
import { BoardStrip } from './BoardStrip';
import type { StripCount, StripStats } from './BoardStrip';
import { DenseTable } from './DenseTable';
import type { BoardPref } from './boardPref';
import type { BookColumn, CellContext } from './columns';
import type { RankedAccount } from './derived';
import type { SortState } from './sort';
import type { BriefPayload } from '../api/types';

// Mock server only: how long the table took to render, so a 1,300-row book can be measured
// (`MOCK_BOOK_ROWS=1300`). Read in the browser console.
const logTableRender: ProfilerOnRenderCallback = (_id, phase, actualDuration) => {
  const actual_duration_ms = Math.round(actualDuration);
  console.debug(`board table rendered. phase=${JSON.stringify(phase)} actual_duration_ms=${actual_duration_ms}`);
};

export function BoardV2({
  rows,
  columns,
  context,
  sort,
  onSort,
  openAccountId,
  onToggleAccount,
  onSelectAccount,
  canEditPrefs,
  pendingPrefId,
  onSetPref,
  brief,
  showBrief,
  stats,
  unreadCount,
  asOf,
  countText,
  briefPanel,
  notificationStrip,
  controls,
  notes,
  groupLabels,
  showRowMenu,
  tableClassName,
  counts,
}: {
  rows: RankedAccount[];
  columns: BookColumn[];
  context: CellContext;
  sort: SortState;
  onSort: (key: string) => void;
  openAccountId: string | null;
  onToggleAccount: (accountId: string) => void;
  onSelectAccount: (accountId: string) => void;
  canEditPrefs: boolean;
  pendingPrefId: string | null;
  onSetPref: (accountId: string, preference: BoardPref) => void;
  brief: BriefPayload | null;
  showBrief: boolean;
  stats: StripStats;
  unreadCount: number;
  asOf: string;
  countText: string;
  briefPanel: ReactNode;
  notificationStrip: ReactNode;
  // The AE view's controls row; the board's are in its sticky filter bar, so it passes none.
  controls?: ReactNode;
  notes: ReactNode;
  // Passed through to the table: the AE view's own section labels, and no Pin / Hide menu.
  groupLabels?: Record<number, string>;
  showRowMenu?: boolean;
  // The AE view's own table class and summary counts, in place of the board's.
  tableClassName?: string;
  counts?: StripCount[];
}) {
  return (
    <div className="boardv2">
      <BoardStrip
        brief={brief}
        showBrief={showBrief}
        stats={stats}
        counts={counts}
        unreadCount={unreadCount}
        asOf={asOf}
        onOpenAccount={onSelectAccount}
        briefPanel={briefPanel}
        notificationStrip={notificationStrip}
      />
      {notes}
      <section className="densepanel">
        <div className="densecontrols">
          {controls}
          <span className="rowcount">{countText}</span>
        </div>
        <Profiler id="board-table" onRender={__MOCK_API__ ? logTableRender : () => {}}>
        <DenseTable
          rows={rows}
          columns={columns}
          context={context}
          sort={sort}
          onSort={onSort}
          openAccountId={openAccountId}
          onToggleAccount={onToggleAccount}
          onSelectAccount={onSelectAccount}
          canEditPrefs={canEditPrefs}
          pendingPrefId={pendingPrefId}
          onSetPref={onSetPref}
          groupLabels={groupLabels}
          showRowMenu={showRowMenu}
          tableClassName={tableClassName}
        />
        </Profiler>
      </section>
    </div>
  );
}
